import React from "react";
import {
    Document,
    Page,
    Text,
    View,
    StyleSheet,
} from "@react-pdf/renderer";

const COLORS = {
    navy: "#092D50",
    deepNavy: "#061F38",
    blue: "#174F7D",
    gold: "#D5A83B",
    white: "#FFFFFF",
    black: "#17212B",
    gray: "#687787",
    lightGray: "#F3F6F8",
    border: "#D5DEE6",
    paleBlue: "#EDF5FB",
    green: "#267342",
    orange: "#B66A13",
    red: "#B42318",
};

const columns = [
    { key: "serial", label: "NO.", width: "3.5%" },
    { key: "name", label: "TEACHER NAME", width: "13%" },
    { key: "contact", label: "CONTACT", width: "9%" },
    { key: "qualification", label: "QUALIFICATION", width: "9%" },
    { key: "status", label: "EMPLOYMENT STATUS / REASON", width: "11%" },
    { key: "subjects", label: "ASSIGNED SUBJECTS", width: "15%" },
    { key: "classes", label: "GRADE CLASSES", width: "12%" },
    { key: "salary", label: "SALARY", width: "8%" },
    { key: "date", label: "DATE ADDED", width: "8%" },
    { key: "dependent", label: "DEPENDENT STUDENT", width: "11.5%" },
];

const styles = StyleSheet.create({
    page: {
        paddingTop: 27,
        paddingBottom: 38,
        paddingHorizontal: 25,
        backgroundColor: COLORS.white,
        fontFamily: "Helvetica",
        color: COLORS.black,
        fontSize: 7,
    },

    topAccent: {
        height: 4,
        backgroundColor: COLORS.gold,
        marginBottom: 12,
        borderRadius: 2,
    },

    header: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        borderBottomWidth: 1.5,
        borderBottomColor: COLORS.navy,
        paddingBottom: 9,
        marginBottom: 10,
    },

    headerLeft: {
        flex: 1,
    },

    reportTitle: {
        fontSize: 16,
        fontWeight: "bold",
        color: COLORS.navy,
        marginBottom: 4,
    },

    reportSubtitle: {
        fontSize: 8,
        color: COLORS.gray,
    },

    headerRight: {
        alignItems: "flex-end",
        marginLeft: 12,
    },

    headerLabel: {
        fontSize: 7,
        color: COLORS.gray,
        marginBottom: 3,
    },

    headerValue: {
        fontSize: 9,
        fontWeight: "bold",
        color: COLORS.navy,
    },

    informationBar: {
        flexDirection: "row",
        alignItems: "center",
        backgroundColor: COLORS.paleBlue,
        borderLeftWidth: 3,
        borderLeftColor: COLORS.gold,
        paddingVertical: 8,
        paddingHorizontal: 10,
        marginBottom: 11,
    },

    informationItem: {
        flex: 1,
    },

    informationLabel: {
        fontSize: 6.5,
        color: COLORS.gray,
        marginBottom: 3,
        fontWeight: "bold",
    },

    informationValue: {
        fontSize: 8,
        color: COLORS.navy,
        fontWeight: "bold",
    },

    sectionHeading: {
        backgroundColor: COLORS.navy,
        paddingVertical: 6,
        paddingHorizontal: 9,
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 5,
    },

    sectionHeadingText: {
        color: COLORS.white,
        fontSize: 8,
        fontWeight: "bold",
    },

    sectionHeadingRight: {
        color: COLORS.gold,
        fontSize: 7,
        fontWeight: "bold",
    },

    table: {
        width: "100%",
        borderWidth: 0.7,
        borderColor: COLORS.border,
    },

    tableHeader: {
        flexDirection: "row",
        backgroundColor: COLORS.navy,
        alignItems: "stretch",
        minHeight: 29,
    },

    tableHeaderCell: {
        justifyContent: "center",
        alignItems: "center",
        paddingHorizontal: 3,
        paddingVertical: 5,
        borderRightWidth: 0.5,
        borderRightColor: "#54708A",
    },

    tableHeaderText: {
        fontSize: 6,
        color: COLORS.white,
        fontWeight: "bold",
        textAlign: "center",
        lineHeight: 1.2,
    },

    tableRow: {
        flexDirection: "row",
        alignItems: "stretch",
        borderBottomWidth: 0.6,
        borderBottomColor: COLORS.border,
        minHeight: 31,
        backgroundColor: COLORS.white,
    },

    tableRowAlternate: {
        backgroundColor: "#F7FAFC",
    },

    tableCell: {
        justifyContent: "center",
        paddingHorizontal: 3,
        paddingVertical: 5,
        borderRightWidth: 0.5,
        borderRightColor: COLORS.border,
    },

    cellText: {
        fontSize: 6.2,
        color: "#344454",
        lineHeight: 1.25,
        textAlign: "left",
    },

    serialText: {
        fontSize: 6.5,
        fontWeight: "bold",
        textAlign: "center",
        color: COLORS.navy,
    },

    teacherName: {
        fontSize: 6.5,
        fontWeight: "bold",
        color: COLORS.navy,
        lineHeight: 1.25,
    },

    statusText: {
        fontSize: 6.2,
        fontWeight: "bold",
        marginBottom: 3,
    },

    statusReason: {
        fontSize: 5.8,
        color: COLORS.gray,
        lineHeight: 1.2,
    },

    summary: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        backgroundColor: COLORS.paleBlue,
        borderWidth: 0.7,
        borderColor: COLORS.border,
        paddingVertical: 7,
        paddingHorizontal: 9,
        marginTop: 9,
    },

    summaryText: {
        fontSize: 7,
        color: COLORS.navy,
        fontWeight: "bold",
    },

    footer: {
        position: "absolute",
        bottom: 15,
        left: 25,
        right: 25,
        borderTopWidth: 0.8,
        borderTopColor: COLORS.border,
        paddingTop: 5,
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
    },

    footerText: {
        fontSize: 6,
        color: COLORS.gray,
    },

    pageNumber: {
        fontSize: 6.5,
        color: COLORS.navy,
        fontWeight: "bold",
    },

    emptyState: {
        padding: 20,
        alignItems: "center",
        borderWidth: 1,
        borderColor: COLORS.border,
    },

    emptyText: {
        fontSize: 9,
        color: COLORS.gray,
    },
});

const safeValue = (value) => {
    if (
        value === undefined ||
        value === null ||
        value === ""
    ) {
        return "N/A";
    }

    if (Array.isArray(value)) {
        return value.length
            ? value.map(safeValue).filter(Boolean).join(", ")
            : "None";
    }

    if (typeof value === "object") {
        return (
            value.name ||
            value.username ||
            value.email ||
            "N/A"
        );
    }

    return String(value);
};

const getStatusColor = (status) => {
    switch (String(status || "").toLowerCase()) {
        case "active":
            return COLORS.green;

        case "removed":
            return COLORS.orange;

        case "fired":
            return COLORS.red;

        default:
            return COLORS.navy;
    }
};

const getStatusLabel = (teacher) => {
    return (
        teacher.employment_status_display ||
        (
            String(teacher.employment_status || "active").toLowerCase() === "fired"
                ? "Sacked / Fired"
                : String(teacher.employment_status || "active")
                    .charAt(0)
                    .toUpperCase() +
                  String(teacher.employment_status || "active").slice(1)
        )
    );
};

const TeacherBioDataDivisionPDF = ({
    teachers = [],
    divisionName = "Selected Division",
    academicYear = "N/A",
}) => {
    const totalTeachers = teachers.length;

    const actualDivisionName =
        typeof divisionName === "object"
            ? (
                divisionName?.name ||
                divisionName?.division_name ||
                divisionName?.title ||
                "Selected Division"
            )
            : divisionName;

    return (
        <Document
            title={`Teacher Biodata - ${actualDivisionName}`}
            author="School Management System"
            subject={`Teacher biodata report for ${actualDivisionName}`}
        >
            <Page
                size="A4"
                orientation="landscape"
                style={styles.page}
                wrap
            >
                <View style={styles.topAccent} />

                {/* REPORT HEADER */}

                <View style={styles.header}>
                    <View style={styles.headerLeft}>
                        <Text style={styles.reportTitle}>
                            Zion Praise Academy - TEACHER BIODATA REGISTER
                        </Text>

                        <Text style={styles.reportSubtitle}>
                            Official Teacher Information and Assignment Report
                        </Text>
                    </View>

                    <View style={styles.headerRight}>
                        <Text style={styles.headerLabel}>
                            ACADEMIC YEAR
                        </Text>

                        <Text style={styles.headerValue}>
                            {safeValue(academicYear)}
                        </Text>
                    </View>
                </View>

                {/* REPORT INFORMATION */}

                <View style={styles.informationBar}>
                    <View style={styles.informationItem}>
                        <Text style={styles.informationLabel}>
                            SELECTED DIVISION
                        </Text>

                        <Text style={styles.informationValue}>
                            {safeValue(actualDivisionName)}
                        </Text>
                    </View>

                    <View style={styles.informationItem}>
                        <Text style={styles.informationLabel}>
                            TOTAL TEACHERS
                        </Text>

                        <Text style={styles.informationValue}>
                            {totalTeachers}
                        </Text>
                    </View>

                    <View style={styles.informationItem}>
                        <Text style={styles.informationLabel}>
                            REPORT TYPE
                        </Text>

                        <Text style={styles.informationValue}>
                            DIVISION TEACHER REGISTER
                        </Text>
                    </View>
                </View>

                {/* TABLE TITLE */}

                <View style={styles.sectionHeading}>
                    <Text style={styles.sectionHeadingText}>
                        COMPLETE TEACHER INFORMATION
                    </Text>

                    <Text style={styles.sectionHeadingRight}>
                        {safeValue(actualDivisionName).toUpperCase()}
                    </Text>
                </View>

                {/* TABLE */}

                {totalTeachers > 0 ? (
                    <View style={styles.table}>
                        {/* REPEATED TABLE HEADER */}

                        <View style={styles.tableHeader} fixed>
                            {columns.map((column) => (
                                <View
                                    key={column.key}
                                    style={[
                                        styles.tableHeaderCell,
                                        { width: column.width },
                                    ]}
                                >
                                    <Text style={styles.tableHeaderText}>
                                        {column.label}
                                    </Text>
                                </View>
                            ))}
                        </View>

                        {/* TEACHER RECORDS */}

                        {teachers.map((teacher, index) => {
                            const status = String(
                                teacher.employment_status || "active"
                            ).toLowerCase();

                            return (
                                <View
                                    key={teacher.id ?? `teacher-${index}`}
                                    style={[
                                        styles.tableRow,
                                        index % 2 === 1
                                            ? styles.tableRowAlternate
                                            : {},
                                    ]}
                                    wrap={false}
                                >
                                    {/* SERIAL NUMBER */}

                                    <View
                                        style={[
                                            styles.tableCell,
                                            {
                                                width: columns[0].width,
                                                alignItems: "center",
                                            },
                                        ]}
                                    >
                                        <Text style={styles.serialText}>
                                            {teacher.serial_number || index + 1}
                                        </Text>
                                    </View>

                                    {/* TEACHER NAME */}

                                    <View
                                        style={[
                                            styles.tableCell,
                                            { width: columns[1].width },
                                        ]}
                                    >
                                        <Text style={styles.teacherName}>
                                            {safeValue(teacher.full_name)}
                                        </Text>
                                    </View>

                                    {/* CONTACT */}

                                    <View
                                        style={[
                                            styles.tableCell,
                                            { width: columns[2].width },
                                        ]}
                                    >
                                        <Text style={styles.cellText}>
                                            {safeValue(teacher.contact)}
                                        </Text>
                                    </View>

                                    {/* QUALIFICATION */}

                                    <View
                                        style={[
                                            styles.tableCell,
                                            { width: columns[3].width },
                                        ]}
                                    >
                                        <Text style={styles.cellText}>
                                            {safeValue(teacher.qualification)}
                                        </Text>
                                    </View>

                                    {/* EMPLOYMENT STATUS AND REASON */}

                                    <View
                                        style={[
                                            styles.tableCell,
                                            { width: columns[4].width },
                                        ]}
                                    >
                                        <Text
                                            style={[
                                                styles.statusText,
                                                {
                                                    color: getStatusColor(status),
                                                },
                                            ]}
                                        >
                                            {getStatusLabel(teacher)}
                                        </Text>

                                        {teacher.status_reason &&
                                            teacher.status_reason !== "N/A" && (
                                                <Text style={styles.statusReason}>
                                                    {`Reason: ${teacher.status_reason}`}
                                                </Text>
                                            )}
                                    </View>

                                    {/* ASSIGNED SUBJECTS */}

                                    <View
                                        style={[
                                            styles.tableCell,
                                            { width: columns[5].width },
                                        ]}
                                    >
                                        <Text style={styles.cellText}>
                                            {safeValue(teacher.subjects_display)}
                                        </Text>
                                    </View>

                                    {/* GRADE CLASSES */}

                                    <View
                                        style={[
                                            styles.tableCell,
                                            { width: columns[6].width },
                                        ]}
                                    >
                                        <Text style={styles.cellText}>
                                            {safeValue(
                                                teacher.grade_classes_display
                                            )}
                                        </Text>
                                    </View>

                                    {/* SALARY */}

                                    <View
                                        style={[
                                            styles.tableCell,
                                            {
                                                width: columns[7].width,
                                                alignItems: "center",
                                            },
                                        ]}
                                    >
                                        <Text style={styles.cellText}>
                                            {safeValue(teacher.salary)}
                                        </Text>
                                    </View>

                                    {/* DATE ADDED */}

                                    <View
                                        style={[
                                            styles.tableCell,
                                            {
                                                width: columns[8].width,
                                                alignItems: "center",
                                            },
                                        ]}
                                    >
                                        <Text style={styles.cellText}>
                                            {safeValue(teacher.date_add)}
                                        </Text>
                                    </View>

                                    {/* DEPENDENT STUDENT */}

                                    <View
                                        style={[
                                            styles.tableCell,
                                            { width: columns[9].width },
                                        ]}
                                    >
                                        <Text style={styles.cellText}>
                                            {safeValue(
                                                teacher.dependent_student
                                            )}
                                        </Text>
                                    </View>
                                </View>
                            );
                        })}
                    </View>
                ) : (
                    <View style={styles.emptyState}>
                        <Text style={styles.emptyText}>
                            No teacher records are available for this division.
                        </Text>
                    </View>
                )}

                {/* REPORT SUMMARY */}

                <View style={styles.summary} wrap={false}>
                    <Text style={styles.summaryText}>
                        TOTAL TEACHERS: {totalTeachers}
                    </Text>

                    <Text style={styles.summaryText}>
                        DIVISION: {safeValue(actualDivisionName)}
                    </Text>

                    <Text style={styles.summaryText}>
                        ACADEMIC YEAR: {safeValue(academicYear)}
                    </Text>
                </View>

                {/* REPEATED PAGE FOOTER */}

                <View style={styles.footer} fixed>
                    <Text style={styles.footerText}>
                        OFFICIAL TEACHER BIODATA REGISTER
                    </Text>

                    <Text
                        style={styles.pageNumber}
                        render={({ pageNumber, totalPages }) =>
                            `PAGE ${pageNumber} OF ${totalPages}`
                        }
                    />
                </View>
            </Page>
        </Document>
    );
};

export default TeacherBioDataDivisionPDF;